import { FC, useState } from 'react'
import { Box, Button, useTheme } from '@mui/material'
import { styled } from '@mui/material/styles'

import { Icon } from 'packages/eid-icons'
import { useTranslation } from 'react-i18next'
import ComputerConnectBtn from 'components/DynamicComponents/ComputerConnectButton'
import classNames from 'classnames'

import useConfiguration from 'useConfiguration'
import ActivateNowButton from 'containers/ListingPage/ActivateNowButton'
import { useHistory } from 'react-router'
import appConfig from 'config'
import MasterLockOverlay from 'packages/eid-ui/MasterLockOverlay'
import { PasswordModal } from 'components/PasswordModal'
import { useAppState } from 'appContext'
import RevokeButton from 'containers/ManageAccess/RevokeButton'

export type PreApprovedItemsAccessTileProps = {
    resource?: any
    assignment?: any
    type: string
    resourceType: any
    showArrows?: boolean
    accessName?: string
    accessTypeLabel?: string
    handleViewRequest: () => void
    showCount?: boolean
    canActivateNow?: boolean
    manageBtnUrl: string
}

const PreApproveCard = styled(Box)(({ theme }) => ({
    boxShadow: '0 4px 16px 0 rgba(0, 0, 0, 0.05)',
    borderRadius: '8px',
    padding: '16px 16px 9px',
    background: theme?.palette?.background?.paper,
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '16px 0',
    borderImageSource:
        'linear-gradient(to right, rgba(1, 174, 143, 0), #04ab936e 7%, #2c83bd47 91%, rgba(48, 127, 193, 0))',
    borderImageSlice: '1',
    borderBottomWidth: '1px',
    borderStyle: 'solid',
    borderTopWidth: '0',
}))

const PreApproveCardLeft = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
})

const PreApproveCardRight = styled(Box)({
    display: 'flex',
    alignItems: 'center',
})

const PreApprovedConnect = styled(Box)({
    display: 'flex',
    '& button': {
        '&:nth-child(1)': {
            paddingLeft: '6px',
            borderRadius: '5px',
            fontSize: '11px',
            height: '3rem',
            '& span': {
                fontSize: '10px',
                '& p': {
                    paddingRight: '5px',
                },
            },
        },
    },
})

const ManageAccessButton = styled(Button)(({ theme }) => ({
    background: theme?.palette?.background?.paper,
    padding: '7px 8px 7px 13px',
    border: `solid 1px ${theme?.palette?.primary?.main}`,
    borderRadius: '4px',
    fontSize: '14px',
    lineHeight: '1',
    color: theme?.palette?.primary?.main,
    marginLeft: '15px',
    textTransform: 'capitalize',
    '& span': {
        fontSize: '14px',
        marginLeft: '0px',
    },
}))

const PreApprovedActivateBtn = styled(Box)(({ theme }) => ({
    display: 'flex',
    '& button': {
        '&:nth-child(1)': {
            background: `${theme?.palette?.secondary?.main} !important`,
            padding: '6px 8px 5px',
            borderRadius: '5px',
            border: `solid 1px ${theme?.palette?.secondary?.main}`,
            fontSize: '11px',
            lineHeight: '1',
            color: theme?.palette?.common?.white,
            '& span': {
                fontSize: '10px',
            },
        },
    },
}))

const ViewRequestBtn = styled(Box)(({ theme }) => ({
    '& button': {
        background: theme?.palette?.common?.white,
        padding: '7px 15px 7px 13px',
        border: `solid 1px ${theme?.palette?.primary?.main}`,
        borderRadius: '16px',
        fontSize: '14px',
        lineHeight: '1',
        color: theme?.palette?.primary?.main,
        marginLeft: '15px',
        '& span': {
            fontSize: '12px',
        },
    },
}))

const PreApproveBtn = styled(Box)({
    border: 'solid 1px #aab0b4',
    fontSize: '12px',
    lineHeight: '1',
    padding: '4px 16px',
    minWidth: '120px',
    textAlign: 'center',
})

const MembershipBtn = styled(PreApproveBtn)(({ theme }) => ({
    background: theme?.palette?.common?.white,
    borderRadius: '5px',
    color: theme?.palette?.tertiary?.main,
}))

const PreApprovedBtn = styled(PreApproveBtn)(({ theme }) => ({
    background: theme?.palette?.common?.white,
    borderRadius: '5px',
    color: theme?.palette?.secondary?.main,
}))

const RequestBtn = styled(PreApproveBtn)(({ theme }) => ({
    background: theme?.palette?.common?.white,
    borderRadius: '5px',
    color: '#1b1f1f',
}))

const PreApproveAdmin = styled(Box)(({ theme }) => ({
    marginLeft: '15px',
    fontSize: '14px',
    color: theme?.palette?.common?.black,
}))

const PreApprovedSection = styled(Box)({
    display: 'flex',
    alignItems: 'center',
})

const TaskBadgeSection = styled(Box)({
    position: 'relative',
})

const TaskBadge = styled('span')(({ theme }) => ({
    position: 'absolute',
    top: '-15px',
    right: '-10px',
    background: theme?.palette?.quaternary?.main,
    height: '15px',
    width: '15px',
    borderRadius: '50%',
    fontSize: '10px',
    color: theme?.palette?.common?.white,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: '1px',
}))

const CustomMasterLock = styled(Box)({
    position: 'relative',
    height: '3rem',
    minHeight: 'auto',
    '& .MuiBox-root': {
        minHeight: 'auto',
    },
    '& div div:nth-child(1)': {},
})

export const PreApprovedItemsAccessTile: FC<PreApprovedItemsAccessTileProps> = (
    props: PreApprovedItemsAccessTileProps,
) => {
    const { t } = useTranslation()
    const theme = useTheme()
    const history = useHistory()
    const {
        resource,
        assignment,
        type,
        resourceType,
        showArrows = true,
        accessTypeLabel,
        accessName,
        handleViewRequest,
        showCount = false,
        canActivateNow,
        manageBtnUrl,
    } = props
    const [{ masterPassword }]: any = useAppState()
    const [open, setOpen] = useState('')
    const { getResourceType } = useConfiguration()

    const handleMasterUnlock = () => {
        setOpen('enterPassword')
    }

    const getManageButton = () => {
        return (
            <ManageAccessButton
                onClick={() =>
                    history.push(`${appConfig.APP_SUBPATH}/${manageBtnUrl}`)
                }
                endIcon={
                    <Icon
                        name="ArrowSmall"
                        width="20px"
                        fill={theme?.palette?.primary?.main}
                        direction="right"
                    />
                }
                size={'medium'}
            >
                {t('Common_Manage')}
            </ManageAccessButton>
        )
    }

    const getActivateNowButton = () => {
        const resourceTypeConfig = getResourceType(resourceType?.name)
        if (canActivateNow && assignment?.resourceAssignment?.resourceId) {
            return (
                <ActivateNowButton
                    resource={{
                        id: assignment?.resourceAssignment?.resourceId,
                        resourceAssignmentId:
                            assignment?.resourceAssignment
                                ?.resourceAssignmentId,
                    }}
                    resourceType={resourceTypeConfig}
                    forCardView={true}
                    width="100px"
                />
            )
        }
    }

    const getConnectButton = () => {
        return (
            <>
                {!masterPassword && (
                    <PasswordModal
                        showDefaultContent={false}
                        open={open}
                        setOpen={setOpen}
                    />
                )}
                {!masterPassword ? (
                    <CustomMasterLock>
                        <MasterLockOverlay
                            btnLabel={t('Common_Unlock')}
                            showMasterLock={!masterPassword}
                            handleMasterUnlock={() => handleMasterUnlock()}
                        >
                            <ComputerConnectBtn
                                parentStyle={{}}
                                showPrefixIcon={false}
                                data={resource}
                                drawerView={false}
                                showCalenderIcon={false}
                            />
                        </MasterLockOverlay>
                    </CustomMasterLock>
                ) : (
                    <ComputerConnectBtn
                        parentStyle={{}}
                        showPrefixIcon={false}
                        data={resource}
                        drawerView={false}
                        showCalenderIcon={false}
                    />
                )}
            </>
        )
    }

    const renderButtons = () => {
        if (type === 'membership') {
            return (
                <>
                    {getActivateNowButton()}
                    {assignment?.assignmentDetails?.isRevokable && (
                        <RevokeButton
                            resourceType={resourceType}
                            item={{
                                resource: resource,
                                assignment: assignment,
                            }}
                            buttonStyles={{ minWidth: '72px' }}
                            postRevoke={() => {}}
                        />
                    )}
                    {getManageButton()}
                </>
            )
        } else if (type === 'preapprovedActivate') {
            return (
                <>
                    <PreApprovedActivateBtn>
                        {getActivateNowButton()}
                    </PreApprovedActivateBtn>
                    {getManageButton()}
                </>
            )
        } else if (type === 'preapprovedConnect') {
            return (
                <>
                    {resource?.isPsmEnabled && (
                        <PreApprovedConnect>
                            {getConnectButton()}
                        </PreApprovedConnect>
                    )}
                    {getManageButton()}
                </>
            )
        } else if (type === 'request') {
            return (
                <ViewRequestBtn>
                    <Button
                        onClick={() => handleViewRequest()}
                        endIcon={
                            <Icon
                                name="ArrowSmall"
                                color={theme?.palette?.primary?.main}
                                width="20px"
                                fill={theme?.palette?.primary?.main}
                                direction="right"
                            />
                        }
                        size={'medium'}
                    >
                        {t('Common_ViewRequest')}
                    </Button>
                </ViewRequestBtn>
            )
        } else {
            return null
        }
    }

    const getButtonComponent = () => {
        switch (type) {
            case 'membership':
                return MembershipBtn
            case 'preapprovedConnect':
            case 'preapprovedActivate':
                return PreApprovedBtn
            case 'request':
                return RequestBtn
            default:
                return PreApproveBtn
        }
    }

    const ButtonComponent = getButtonComponent()

    return (
        <>
            <PreApproveCard>
                <PreApproveCardLeft>
                    <PreApprovedSection>
                        <ButtonComponent>{accessTypeLabel}</ButtonComponent>
                        {showArrows ? (
                            <Box>
                                <Icon name="ArrowIconGreen" />
                            </Box>
                        ) : null}
                    </PreApprovedSection>
                    <TaskBadgeSection>
                        <PreApproveAdmin>{accessName}</PreApproveAdmin>
                        {showCount && (
                            <TaskBadge>{resource?.checkOutCount}</TaskBadge>
                        )}
                    </TaskBadgeSection>
                </PreApproveCardLeft>
                <PreApproveCardRight>{renderButtons()}</PreApproveCardRight>
            </PreApproveCard>
        </>
    )
}
